import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom';
import type {
  DetectOverflowOptions,
  Placement as FloatingUIPlacement,
} from '@floating-ui/react-dom';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import styles from './ContentPreviewPopOver.module.scss';
import FloatingArrow, { ARROW_SIZE } from './FloatingArrow';
import ErrorBoundary from './ErrorBoundary';
import contentPreviewPopOverSize from '../utils/contentPreviewPopOverSize';

export const SCREEN_EDGE_PADDING = 12;

const DETECT_OVERFLOW_OPTIONS: Partial<DetectOverflowOptions> = {
  padding: SCREEN_EDGE_PADDING,
};

interface UseHoverOptions {
  onOpenChange: (newOpen: boolean) => void;
  longHoverDelay?: boolean;
}

const useHover = ({ onOpenChange, longHoverDelay }: UseHoverOptions) => {
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openDelay = longHoverDelay ? 500 : 200;
  const closeDelay = 100;

  const setHoverIntent = useCallback(
    (newOpen: boolean, delay: number) => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      hoverTimeoutRef.current = setTimeout(() => {
        onOpenChange(newOpen);
      }, delay);
    },
    [onOpenChange],
  );

  useEffect(
    () => () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    },
    [],
  );

  return useMemo(
    () => ({
      onMouseEnter: () => {
        setHoverIntent(true, openDelay);
      },
      onMouseLeave: () => {
        setHoverIntent(false, closeDelay);
      },
    }),
    [setHoverIntent, openDelay, closeDelay],
  );
};

export type Placement = FloatingUIPlacement;

interface UncontrolledContentPreviewPopOverProps {
  hidden?: boolean;
  display?: 'block' | 'inline-block' | 'inline';
  allowCrossAxisFlip?: boolean;
  offset?: number;
  minWidth?: number;
  minHeight?: number;
  children: ReactNode;
  popOver: ReactNode;
  placement?: Placement;
  longHoverDelay?: boolean;
  reference?: React.RefObject<HTMLElement>;
}

const UncontrolledContentPreviewPopOver = (
  props: UncontrolledContentPreviewPopOverProps,
) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <ControlledContentPreviewPopOver
      {...props}
      open={isOpen}
      onOpenChange={setIsOpen}
    />
  );
};

interface ControlledContentPreviewPopOverProps
  extends UncontrolledContentPreviewPopOverProps {
  open: boolean;
  onOpenChange: (newOpen: boolean) => void;
}

const ControlledContentPreviewPopOver = ({
  display = 'block',
  hidden,
  allowCrossAxisFlip,
  offset: offsetAmount = 2,
  minWidth = 325,
  minHeight = 250,
  children,
  popOver,
  placement = 'right',
  longHoverDelay,
  open,
  onOpenChange,
  reference,
}: ControlledContentPreviewPopOverProps) => {
  const arrowRef = useRef<HTMLDivElement | null>(null);

  const middleware = useMemo(
    () => [
      offset(offsetAmount + ARROW_SIZE),
      shift(DETECT_OVERFLOW_OPTIONS),
      contentPreviewPopOverSize({
        ...DETECT_OVERFLOW_OPTIONS,
      }),
      flip({
        fallbackAxisSideDirection: allowCrossAxisFlip ? 'end' : 'none',
        ...DETECT_OVERFLOW_OPTIONS,
      }),
      arrow({ element: arrowRef, padding: 12 }),
    ],
    [offsetAmount, allowCrossAxisFlip],
  );

  const {
    refs,
    floatingStyles,
    middlewareData: { arrow: arrowData },
    placement: actualPlacement,
  } = useFloating({
    placement,
    middleware,
    whileElementsMounted: autoUpdate,
  });

  useLayoutEffect(() => {
    if (reference) {
      refs.setReference(reference.current);
    }
  }, [reference, refs]);

  const eventListeners = useHover({ onOpenChange, longHoverDelay });

  const [hasBeenOpened, setHasBeenOpened] = useState(open);
  if (open && !hasBeenOpened) {
    setHasBeenOpened(true);
    return null;
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.inline]: display === 'inline',
        [styles.inlineBlock]: display === 'inline-block',
      })}
      {...eventListeners}
    >
      <div
        ref={reference ? undefined : refs.setReference}
        className={styles.reference}
      >
        {children}
      </div>
      {(open || hasBeenOpened) &&
        createPortal(
          <ErrorBoundary name="contentPreviewPopOver">
            <div
              ref={refs.setFloating}
              style={{ ...floatingStyles, minHeight, minWidth }}
              className={classNames(styles.popOver, {
                [styles.hidden]: hidden || !open,
              })}
            >
              <div className={styles.popOverContent}>{popOver}</div>
              <FloatingArrow
                ref={arrowRef}
                placement={actualPlacement}
                arrowData={arrowData}
              />
            </div>
          </ErrorBoundary>,
          document.body,
        )}
    </div>
  );
};

type ContentPreviewPopOverProps =
  | (
      | ControlledContentPreviewPopOverProps
      | UncontrolledContentPreviewPopOverProps
    ) & {
      hideOnMobile?: boolean;
    };

const ContentPreviewPopOver = ({
  hideOnMobile,
  ...props
}: ContentPreviewPopOverProps) => {
  const shouldHide = useIsSize(Sizes.SM, { skipResizeListener: !hideOnMobile });

  if (shouldHide && hideOnMobile) {
    return <>{props.children}</>;
  }

  return 'onOpenChange' in props ? (
    <ControlledContentPreviewPopOver {...props} />
  ) : (
    <UncontrolledContentPreviewPopOver {...props} />
  );
};

export default ContentPreviewPopOver;
